import {
  Badge,
  Banner,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  FormField,
  Icon,
  IconButton,
  Input,
  QrCode,
  SettingsRow,
  SettingsSection,
  StatusBadge,
  toast,
  useIsPhone,
} from '@nexus/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { LoginSecurity, PasskeySummary } from '../api/types';
import { formatDate } from '../i18n/format';
import {
  addPasskey,
  isPasskeyCancelled,
  isPasskeyKnown,
  passkeysSupported,
} from '../session/passkey';
import { NewPasswordFields, newPasswordProblem, usePasswordError } from '../session/password';
import { sessionQuery, useSession } from '../session/session';

export const loginSecurityQuery = queryOptions({
  queryKey: ['login-security'],
  queryFn: ({ signal }) => api.get<LoginSecurity>('/auth/security', { signal }),
});

/** Second factor and session changed on the server: permissions and banner follow. */
function useRefreshLogin() {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries(loginSecurityQuery),
      client.invalidateQueries(sessionQuery),
    ]);
  };
}

/** Errors of the dialogs go to a toast, the dialog stays open for another try. */
function useLoginError() {
  const errorText = usePasswordError();
  return (error: unknown) => toast.error(errorText(error));
}

/**
 * Password and authenticator app of a login without SSO (ADR-0041), section of the profile. SSO
 * accounts manage both at their provider.
 */
export function LoginSecuritySection() {
  const { t } = useTranslation();
  const security = useQuery(loginSecurityQuery).data;
  const [dialog, setDialog] = useState<
    'password' | 'setup' | 'codes' | 'remove' | 'passkey' | null
  >(null);
  const [removing, setRemoving] = useState<PasskeySummary | null>(null);
  if (!security || (security.method !== 'password' && security.method !== 'passkey')) return null;
  const close = (open: boolean) => !open && setDialog(null);
  // Once the account has a second factor, further ones are managed from a login with MFA only.
  const hasFactor = security.twoFactor || security.passkeys.length > 0;
  const canChangeFactors = security.mfa || !hasFactor;

  return (
    <SettingsSection title={t('profile.security.title')}>
      <SettingsRow
        label={t('profile.security.password')}
        description={
          security.loginEmail
            ? t('profile.security.passwordHint', { email: security.loginEmail })
            : undefined
        }
      >
        <Button icon="key-round" onClick={() => setDialog('password')}>
          {t('profile.security.change')}
        </Button>
      </SettingsRow>
      <SettingsRow
        label={t('profile.security.app')}
        description={t(
          security.twoFactor
            ? 'profile.security.appOn'
            : canChangeFactors
              ? 'profile.security.appOff'
              : 'profile.security.needsMfa',
        )}
        badge={
          security.twoFactor ? (
            <StatusBadge status="success">{t('profile.security.on')}</StatusBadge>
          ) : null
        }
      >
        {security.twoFactor
          ? security.mfa && (
              <>
                <Button onClick={() => setDialog('codes')}>{t('profile.security.newCodes')}</Button>
                <Button variant="danger-ghost" onClick={() => setDialog('remove')}>
                  {t('profile.security.remove')}
                </Button>
              </>
            )
          : canChangeFactors && (
              <Button variant="primary" icon="shield-check" onClick={() => setDialog('setup')}>
                {t('profile.security.setUp')}
              </Button>
            )}
      </SettingsRow>
      <SettingsRow
        label={t('profile.security.passkeys')}
        description={t(
          !passkeysSupported()
            ? 'profile.security.passkeysUnsupported'
            : canChangeFactors
              ? 'profile.security.passkeysHint'
              : 'profile.security.needsMfa',
        )}
        badge={security.passkeys.length > 0 ? <Badge>{security.passkeys.length}</Badge> : null}
      >
        {passkeysSupported() && canChangeFactors && (
          <Button icon="fingerprint" onClick={() => setDialog('passkey')}>
            {t('profile.security.addPasskey')}
          </Button>
        )}
      </SettingsRow>
      {security.passkeys.length > 0 && (
        <PasskeyList
          passkeys={security.passkeys}
          onRemove={security.mfa ? setRemoving : undefined}
        />
      )}
      {dialog === 'password' && <ChangePasswordDialog onOpenChange={close} />}
      {dialog === 'setup' && <SetupDialog onOpenChange={close} />}
      {dialog === 'codes' && <NewCodesDialog onOpenChange={close} />}
      {dialog === 'passkey' && <AddPasskeyDialog onOpenChange={close} />}
      <RemoveDialog open={dialog === 'remove'} onOpenChange={close} />
      <RemovePasskeyDialog passkey={removing} onClose={() => setRemoving(null)} />
    </SettingsSection>
  );
}

function ChangePasswordDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const reportError = useLoginError();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [touched, setTouched] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      api.post('/auth/password/change', { currentPassword: current, newPassword: password }),
    onSuccess: () => {
      toast.success(t('profile.security.changed'));
      onOpenChange(false);
    },
    onError: reportError,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (current && !newPasswordProblem(password, repeat)) change.mutate();
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={t('profile.security.change')}
        description={t('profile.security.changeHint')}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form={formId} variant="primary" loading={change.isPending}>
              {t('profile.security.save')}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
          <FormField
            label={t('profile.security.current')}
            required
            error={touched && !current ? t('ui.required') : undefined}
          >
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            )}
          </FormField>
          <NewPasswordFields
            password={password}
            repeat={repeat}
            onPassword={setPassword}
            onRepeat={setRepeat}
            touched={touched}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Password of the account, asked before a change to the second factor. */
function PasswordField({
  value,
  onChange,
  touched,
}: {
  value: string;
  onChange: (value: string) => void;
  touched: boolean;
}) {
  const { t } = useTranslation();
  return (
    <FormField
      label={t('profile.security.current')}
      required
      error={touched && !value ? t('ui.required') : undefined}
    >
      {(field) => (
        <Input
          {...field}
          type="password"
          autoComplete="current-password"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FormField>
  );
}

/** The key of the app from its link, in groups of four for typing it by hand. */
function secretOf(totpUri: string): string {
  const secret = new URL(totpUri).searchParams.get('secret') ?? '';
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

/** Three steps: password, scan and confirm with a code, keep the backup codes. */
function SetupDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const phone = useIsPhone();
  const reportError = useLoginError();
  const refresh = useRefreshLogin();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const enable = useMutation({
    mutationFn: () =>
      api.post<{ totpUri: string; backupCodes: string[] }>('/auth/two-factor/enable', {
        password,
      }),
    onSuccess: () => setTouched(false),
    onError: reportError,
  });
  const confirm = useMutation({
    mutationFn: () => api.post('/auth/two-factor/confirm', { code }),
    onSuccess: async () => {
      setConfirmed(true);
      toast.success(t('profile.security.enabled'));
      await refresh();
    },
    onError: reportError,
  });
  const setup = enable.data;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!setup && password) enable.mutate();
    if (setup && /^\d{6}$/.test(code)) confirm.mutate();
  }

  if (setup && confirmed) {
    return (
      <BackupCodesDialog
        codes={setup.backupCodes}
        description={t('profile.security.codesHint')}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={t('profile.security.setupTitle')}
        description={setup ? t('profile.security.scan') : t('profile.security.confirmPassword')}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              loading={enable.isPending || confirm.isPending}
            >
              {setup ? t('profile.security.confirm') : t('profile.security.continue')}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
          {setup ? (
            <>
              <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <QrCode
                  value={setup.totpUri}
                  label={t('profile.security.qrLabel')}
                  className="w-44 shrink-0 rounded-md border border-line"
                />
                <div className="flex min-w-0 flex-col gap-3">
                  {phone && (
                    <Button asChild icon="external-link" className="self-start">
                      <a href={setup.totpUri}>{t('profile.security.openApp')}</a>
                    </Button>
                  )}
                  <div>
                    <p className="text-sm text-fg-muted">{t('profile.security.secret')}</p>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-fg">{secretOf(setup.totpUri)}</code>
                      <IconButton
                        icon="copy"
                        size="sm"
                        label={t('profile.security.copySecret')}
                        onClick={() =>
                          void navigator.clipboard
                            .writeText(secretOf(setup.totpUri).replace(/ /g, ''))
                            .then(() => toast.success(t('profile.security.copied')))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <FormField
                label={t('profile.security.code')}
                required
                error={
                  touched && !/^\d{6}$/.test(code) ? t('login.twoFactor.totp.invalid') : undefined
                }
              >
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="font-mono tracking-widest md:w-40"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  />
                )}
              </FormField>
            </>
          ) : (
            <PasswordField value={password} onChange={setPassword} touched={touched} />
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** New backup codes; the old ones stop working. */
function NewCodesDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const reportError = useLoginError();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const generate = useMutation({
    mutationFn: () =>
      api.post<{ backupCodes: string[] }>('/auth/two-factor/backup-codes', { password }),
    onError: reportError,
  });

  if (generate.data) {
    return (
      <BackupCodesDialog
        codes={generate.data.backupCodes}
        description={t('profile.security.newCodesHint')}
        onOpenChange={onOpenChange}
      />
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (password) generate.mutate();
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={t('profile.security.newCodes')}
        description={t('profile.security.newCodesConfirm')}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form={formId} variant="primary" loading={generate.isPending}>
              {t('profile.security.generate')}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={submit} noValidate>
          <PasswordField value={password} onChange={setPassword} touched={touched} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Shows backup codes once, to copy or download. */
function BackupCodesDialog({
  codes,
  description,
  onOpenChange,
}: {
  codes: string[];
  description: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const text = codes.join('\n');

  function download() {
    const header = t('profile.security.codesFile', { company: session.company?.name ?? '' });
    const url = URL.createObjectURL(new Blob([`${header}\n\n${text}\n`], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'staw-app-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={t('profile.security.codesTitle')}
        description={description}
        footer={
          <>
            <Button icon="download" onClick={download}>
              {t('profile.security.download')}
            </Button>
            <Button
              icon="copy"
              onClick={() =>
                void navigator.clipboard
                  .writeText(text)
                  .then(() => toast.success(t('profile.security.copied')))
              }
            >
              {t('profile.security.copy')}
            </Button>
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              {t('profile.security.done')}
            </Button>
          </>
        }
      >
        <ul
          aria-label={t('profile.security.codesTitle')}
          className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-line bg-sunken p-4 font-mono text-fg"
        >
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

/** The passkeys of the account with name, dates and removal. Never key or credential ID. */
function PasskeyList({
  passkeys,
  onRemove,
}: {
  passkeys: PasskeySummary[];
  /** Missing without MFA: the password alone removes no passkey. */
  onRemove?: (passkey: PasskeySummary) => void;
}) {
  const { t, i18n } = useTranslation();
  const date = (value: string) => formatDate(new Date(value), i18n.language);
  return (
    <ul
      aria-label={t('profile.security.passkeys')}
      className="mb-4 divide-y divide-line rounded-md border border-line"
    >
      {passkeys.map((passkey) => {
        const name = passkey.name ?? t('profile.security.passkeyName');
        return (
          <li key={passkey.id} className="flex items-center gap-3 px-3 py-2.5">
            <Icon name="fingerprint" className="shrink-0 text-fg-subtle" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium text-fg">{name}</span>
                {passkey.synced && <Badge>{t('profile.security.synced')}</Badge>}
              </div>
              <p className="text-sm text-fg-muted">
                {t('profile.security.passkeyCreated', { date: date(passkey.createdAt) })}
                {' · '}
                {passkey.lastUsedAt
                  ? t('profile.security.passkeyUsed', { date: date(passkey.lastUsedAt) })
                  : t('profile.security.passkeyNeverUsed')}
              </p>
            </div>
            {onRemove && (
              <IconButton
                icon="trash-2"
                label={t('profile.security.removePasskey', { name })}
                onClick={() => onRemove(passkey)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Two steps: the password, then the prompt of browser or password manager (1Password, Apple,
 * Google, Windows Hello) that creates the passkey.
 */
function AddPasskeyDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const errorText = usePasswordError();
  const refresh = useRefreshLogin();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const add = useMutation({
    mutationFn: () => addPasskey(password),
    onSuccess: async () => {
      toast.success(t('profile.security.passkeyAdded'));
      await refresh();
      onOpenChange(false);
    },
  });
  const failure =
    add.error && !isPasskeyCancelled(add.error)
      ? isPasskeyKnown(add.error)
        ? t('profile.security.passkeyKnown')
        : errorText(add.error)
      : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (password) add.mutate();
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={t('profile.security.addPasskey')}
        description={t('profile.security.addPasskeyHint')}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form={formId} variant="primary" loading={add.isPending}>
              {t('profile.security.continue')}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-3">
          {failure && (
            <Banner tone="danger" role="alert">
              {failure}
            </Banner>
          )}
          <PasswordField value={password} onChange={setPassword} touched={touched} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Removing a passkey needs the password; the other sessions of the account end. */
function RemovePasskeyDialog({
  passkey,
  onClose,
}: {
  passkey: PasskeySummary | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const refresh = useRefreshLogin();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const remove = useMutation({
    mutationFn: (id: string) => api.post(`/auth/passkeys/${id}/remove`, { password }),
    onSuccess: async () => {
      toast.success(t('profile.security.passkeyRemoved'));
      await refresh();
    },
  });

  return (
    <ConfirmDialog
      open={passkey !== null}
      onOpenChange={(next) => {
        if (!next) {
          setPassword('');
          setTouched(false);
          remove.reset();
          onClose();
        }
      }}
      tone="danger"
      title={t('profile.security.removePasskeyTitle', {
        name: passkey?.name ?? t('profile.security.passkeyName'),
      })}
      description={t('profile.security.removePasskeyHint')}
      confirmLabel={t('profile.security.remove')}
      onConfirm={() => {
        setTouched(true);
        // A rejection keeps the dialog open.
        return password && passkey
          ? remove.mutateAsync(passkey.id)
          : Promise.reject(new Error('Password missing'));
      }}
    >
      <div className="flex flex-col gap-3">
        {remove.error && (
          <Banner tone="danger" role="alert">
            {errorText(remove.error)}
          </Banner>
        )}
        <PasswordField value={password} onChange={setPassword} touched={touched} />
      </div>
    </ConfirmDialog>
  );
}

/** Removing the app needs the password again; afterwards the login asks for the password only. */
function RemoveDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const refresh = useRefreshLogin();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.post('/auth/two-factor/disable', { password }),
    onSuccess: async () => {
      toast.success(t('profile.security.removed'));
      await refresh();
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setPassword('');
          setTouched(false);
          remove.reset();
        }
        onOpenChange(next);
      }}
      tone="danger"
      title={t('profile.security.removeTitle')}
      description={t('profile.security.removeHint')}
      confirmLabel={t('profile.security.remove')}
      onConfirm={() => {
        setTouched(true);
        // A rejection keeps the dialog open.
        return password ? remove.mutateAsync() : Promise.reject(new Error('Password missing'));
      }}
    >
      <div className="flex flex-col gap-3">
        {remove.error && (
          <Banner tone="danger" role="alert">
            {errorText(remove.error)}
          </Banner>
        )}
        <PasswordField value={password} onChange={setPassword} touched={touched} />
      </div>
    </ConfirmDialog>
  );
}
